import { useCallback,useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import { z } from "zod";

import { toAdministrationMethodCode } from "features/immunizations/constants/administrationMethods";
import { useHealthlinkVaccines } from "features/immunizations/hooks/useHealthlinkVaccines";
import { useVaccineBatches } from "features/immunizations/hooks/useVaccineBatches";
import { useVaccineRiskFactors } from "features/immunizations/hooks/useVaccineRiskFactors";
import {
  applyHealthcareWorkerRiskCode,
  withoutHealthcareWorkerOption,
} from "features/immunizations/utils/healthcareWorkerRiskCode";
import { mergeDefaultedRiskCodes } from "features/immunizations/utils/riskFactorSelection";
import { VACCINATION_PROGRAMME } from "features/immunizations/utils/vaccinationProgramme";
import {
  findBatchById,
  getBatchFormValues,
  toRecordedBatchOption,
} from "features/immunizations/utils/vaccineBatches";
import { useStaffInfo } from "features/practice/hooks/useStaffInfo";
import { formatDateISO } from "utils/dateFormatter";

import {
  DEFAULT_VALUES,
  getManufacturerByMvxCode,
  REACTION_YES,
} from "../constants";
import { isHealthcareWorkerQuestionAsked } from "../utils/healthcareWorkerQuestion";

// The HSE supplies the vial, so a dose is transcription rather than a judgement, and
// nothing checked the transcription: dev holds 20 distinct doses for Pneumovax 23,
// including 123, 334 and 567 ml, and a Comirnaty row reading 1231231 ml (KIN-1679).
// Five millilitres is far above any single vaccine dose in use and is deliberately
// generous — this rejects the absurd, it does not pretend to know the right dose for a
// product. The real fix is deriving the dose from the product, which needs the backend
// to carry it; until then this is the backstop, and it stays useful afterwards for
// whatever remains hand-entered.
const MAX_PLAUSIBLE_DOSE_ML = 5;

const formSchema = z
  .object({
    reaction: z.string().min(1, { message: "You must choose reaction." }),
    type: z.string().min(1, { message: "You must choose type." }),
    vaccineName: z.string().min(1, { message: "Enter the vaccine name." }),
    /*
     * Required for a dose this practice gave, and enforced in the superRefine below rather
     * than here, because a dose given somewhere else has none of them (KIN-1883). Nobody
     * transcribing a parent's account of a vaccination in another country knows the batch
     * number or which arm it went into, and demanding them is what made the ordinary path
     * unusable for that.
     */
    batchNo: z.string().optional(),
    /*
     * The registry batch the dose was given from, when it was picked from the list rather
     * than typed (KIN-1740). Optional on purpose: a manual entry has no registry behind
     * it, a dose given elsewhere has no vial of ours at all, and a batch that is simply
     * not in the list still has to be enterable. `batchNo` above stays the field that is
     * required and submitted; this only says where it came from.
     */
    vaccineBatchId: z.string().optional(),
    administrationMethod: z.string().optional(),
    dateGiven: z.string().min(1, { message: "Enter the date given." }),
    expiryDate: z.string().optional(),
    siteGiven: z.string().optional(),
    dose: z.string().min(1, { message: "Enter the dose." }),
    reactionDate: z.string().optional(),
    notes: z.string().optional(),
    vaccineCodeId: z.string().optional(),
    vaccineCodeName: z.string().optional(),
    vaccineCodeCvxCode: z.string().optional(),
    vaccineCodeMvxCode: z.string().optional(),
    vaccineCodeSnomed: z.string().optional(),
    specVersion: z.string().optional(),
    doseNumberCode: z.string().optional(),
    administrationNotesCode: z.string().optional(),
    additionalFactors: z.array(z.string()).optional(),
    immunocompromisedAssessment: z.string().optional(),
    // Mirrors whether the risk-factor response offered any IC statuses. A superRefine
    // only sees form data, and the answer is only mandatory when there is a list to
    // answer from — otherwise the field is hidden and the error unfixable.
    immunocompromisedOptionsAvailable: z.boolean().optional(),
    // Out-of-schedule MMR (KIN-1875). The programme is held as a form value and turned
    // into `vaccinationProgrammeCode` on submit, because Primary Childhood sends nothing
    // at all rather than a code of its own.
    vaccinationProgramme: z.string().optional(),
    outbreakCode: z.string().optional(),
    /*
     * A dose this practice did not give (KIN-1883). Held as a plain boolean here and turned
     * into `administeredElsewhereAt` on submit: the wire has no flag, the timestamp being
     * non-null is the marker, and the backend keys every one of its guarantees off that.
     */
    givenElsewhere: z.boolean().optional(),
    givenElsewhereBy: z
      .string()
      .max(200, { message: "Keep this under 200 characters." })
      .optional(),
    ambulatoryStatus: z.string().optional(),
    riskFactorCodes: z.array(z.string()).optional(),
    clinicallyNecessary: z.boolean().optional(),
    consentGivenCode: z.string().min(1, { message: "You must choose consent." }),
    ethnicityCode: z.string().optional(),
    vaccinator: z.string().optional(),
    healthcareWorker: z.boolean().nullable().optional(),
    // Not an input. The Healthcare Worker question is asked on age at the vaccination
    // date, so the rule needs the date of birth, and a superRefine only sees form data.
    patientDateOfBirth: z.string().nullable().optional(),
    pciScheduleCode: z.string().optional(),
    pciVaccineType: z.string().optional(),
    vaccineManufacturer: z
      .string()
      .min(1, { message: "Enter the manufacturer." }),
  })
  .superRefine((data, ctx) => {
    /*
     * The five fields that are only meaningful when we gave the dose ourselves. The product
     * and the date are deliberately not among them and stay required above: the product is
     * what fills the schedule slot, so a record without one leaves the child on the recall
     * list for a vaccine they have already had — the exact false work KIN-1883 removes —
     * and the date drives the interval rules.
     */
    if (!data.givenElsewhere) {
      const requiredWhenWeGaveIt = [
        ["batchNo", "Enter the batch no."],
        ["administrationMethod", "You must choose administration method."],
        ["expiryDate", "Enter the expiry date."],
        ["siteGiven", "You must choose site given."],
      ];

      requiredWhenWeGaveIt.forEach(([field, message]) => {
        if (!data[field] || !data[field].trim()) {
          ctx.addIssue({ path: [field], code: z.ZodIssueCode.custom, message });
        }
      });
    }

    if (data.vaccineCodeId && (!data.doseNumberCode || !data.doseNumberCode.trim())) {
      ctx.addIssue({
        path: ["doseNumberCode"],
        code: z.ZodIssueCode.custom,
        message: "You must choose dose number.",
      });
    }

    if (data.type === "Influenza" && (!data.notes || !data.notes.trim())) {
      ctx.addIssue({
        path: ["notes"],
        code: z.ZodIssueCode.custom,
        message: "Enter notes for Influenza vaccines.",
      });
    }

    // A HealthLink vaccine is one chosen from the catalogue; a manual entry has no
    // product and none of the NIIS reporting fields apply to it.
    const isCatalogueVaccine = !!(data.vaccineCodeId && data.vaccineCodeId.trim());
    const isFluOrPneumo =
      data.type === "Influenza" ||
      data.type === "Pneumococcal" ||
      data.type === "Pneumococcol";
    // Dose 1 → Course Completion required; Dose 2+ → Clinical Necessity.
    //
    // Both are HealthLink-only, matching where AdministrationSection renders them
    // (`isFluOrPneumoHealthlink`). Without that condition the schema demanded a field
    // the manual form does not show, so Save did nothing and said nothing — a dead end
    // with no way out, which is how it was found: a patient whose record cannot reach
    // NIIS now opens the form in Manual mode.
    const requiresCourseCompletion =
      isCatalogueVaccine && isFluOrPneumo && data.doseNumberCode === "1";
    const requiresClinicalNecessity =
      isCatalogueVaccine && isFluOrPneumo && data.doseNumberCode !== "1";

    if (
      requiresCourseCompletion &&
      !["X0070-1", "X0070-2"].includes(data.administrationNotesCode)
    ) {
      ctx.addIssue({
        path: ["administrationNotesCode"],
        code: z.ZodIssueCode.custom,
        message: "Select course completion.",
      });
    }

    if (requiresClinicalNecessity && data.clinicallyNecessary === undefined) {
      ctx.addIssue({
        path: ["clinicalNecessityDisplay"],
        code: z.ZodIssueCode.custom,
        message: "Select clinical necessity.",
      });
    }

    // A vaccine is identified either by a catalogue product or, for a manual entry, by
    // a name — a dose given abroad, a historical record, or a product HSE has issued no
    // code for. Requiring the product would break manual entry, which is a supported
    // feature; requiring neither leaves a record that says nothing about what was
    // administered and can never be submitted, and three such records exist on dev
    // (KIN-1700).
    //
    // `vaccineName` is already non-empty by the field rule above, but min(1) accepts a
    // single space, which identifies nothing. This asks for a non-blank name, matching
    // the server-side @VaccineIdentified constraint rather than sitting a hair looser
    // than it.
    const hasVaccineName = !!(data.vaccineName && data.vaccineName.trim());

    if (!isCatalogueVaccine && !hasVaccineName) {
      ctx.addIssue({
        path: ["vaccineName"],
        code: z.ZodIssueCode.custom,
        message: "Select a vaccine from the list, or enter a vaccine name.",
      });
    }

    if (
      isHealthcareWorkerQuestionAsked({
        vaccineType: data.type,
        dateOfBirth: data.patientDateOfBirth,
        dateGiven: data.dateGiven,
      }) &&
      data.healthcareWorker !== true &&
      data.healthcareWorker !== false
    ) {
      ctx.addIssue({
        path: ["healthcareWorker"],
        code: z.ZodIssueCode.custom,
        message: "Please select an answer for the HCW question",
      });
    }

    // The backend HARD_STOPs an Outbreak dose with no campaign named (OUTBREAK_CODE_REQUIRED).
    // Caught here so the clinician fixes it in the form rather than at submission.
    if (
      data.vaccinationProgramme === VACCINATION_PROGRAMME.outbreak &&
      !data.outbreakCode
    ) {
      ctx.addIssue({
        path: ["outbreakCode"],
        code: z.ZodIssueCode.custom,
        message: "Select the outbreak this vaccination was given for.",
      });
    }

    if (
      data.immunocompromisedOptionsAvailable &&
      isCatalogueVaccine &&
      !data.immunocompromisedAssessment
    ) {
      ctx.addIssue({
        path: ["immunocompromisedAssessment"],
        code: z.ZodIssueCode.custom,
        message: "Please select a response to the Immunocompromised question",
      });
    }

    // Belt and braces behind the disabled field: the payload is assembled from form
    // state, so a value could survive a path the component does not police — a record
    // opened with both already set, before the clearing effect has run.
    if (data.reactionDate && data.reaction !== REACTION_YES) {
      ctx.addIssue({
        path: ["reactionDate"],
        code: z.ZodIssueCode.custom,
        message: "A reaction date needs Reaction set to Yes.",
      });
    }

    if (data.dose && data.dose.trim()) {
      const doseMl = Number(data.dose);
      const isPlausible =
        Number.isFinite(doseMl) && doseMl > 0 && doseMl <= MAX_PLAUSIBLE_DOSE_ML;

      if (!isPlausible) {
        ctx.addIssue({
          path: ["dose"],
          code: z.ZodIssueCode.custom,
          message: `Dose must be a number greater than 0 and no more than ${MAX_PLAUSIBLE_DOSE_ML} ml.`,
        });
      }
    }

    if (isCatalogueVaccine && data.dateGiven && data.expiryDate) {
      const dateGiven = new Date(data.dateGiven);
      const expiryDate = new Date(data.expiryDate);
      if (dateGiven > expiryDate) {
        ctx.addIssue({
          path: ["dateGiven"],
          code: z.ZodIssueCode.custom,
          message: "Date given cannot be after the vaccine expiry date.",
        });
      }
    }

  });

// Named for what it produces. It used to be `formatDate`, which shadowed the import of
// the same name from utils/dateFormatter — that one formats DD-MM-YYYY, and reading this
// file as though it were the import is what produced KIN-1716's report of a format bug
// that does not exist. Both are ISO here, and every other path through these fields is
// ISO too: DateField builds `${year}-${month}-${day}`, and the backend binds both
// columns with `pattern = "yyyy-MM-dd"`.
const toIsoDate = (date) => dayjs(date).format("YYYY-MM-DD");

// Today for a new record, the record's own value on an edit.
//
// `dateGiven` defaults to today because that is what recording a vaccination means. Pass
// `defaultToToday: false` for a field where today is the one value guaranteed to be
// wrong.
const getInitialDate = (date, { defaultToToday = true } = {}) => {
  if (date) {
    return formatDateISO(date);
  }

  return defaultToToday ? toIsoDate(new Date()) : "";
};

export const useVaccineForm = ({ vaccine, currentPatientId, patientDateOfBirth }) => {
  const isEditMode = !!vaccine?.id;
  const { data: staffInfo } = useStaffInfo();

  const [selectedSnomedCode, setSelectedSnomedCode] = useState(
    isEditMode ? (vaccine?.vaccineCodeCvxCode || null) : null
  );
  const [selectedVaccineType, setSelectedVaccineType] = useState(
    isEditMode ? (vaccine?.type || null) : null
  );

  const getDefaultVaccinator = useCallback(() => {
    if (isEditMode && vaccine?.administrationStaffId) {
      return vaccine.administrationStaffId.toString();
    }
    return staffInfo?.staffMember?.id?.toString() || "";
  }, [isEditMode, vaccine?.administrationStaffId, staffInfo?.staffMember?.id]);

  // The route last filled from a product, so a stale one can be told from a typed one.
  const filledRouteRef = useRef(null);

  /*
   * The batch whose number and expiry are already in the form, so a batch the clinician
   * has just chosen can be told from one that was simply reopened (KIN-1740).
   *
   * Seeded with the record's own batch on an edit, which is what stops the fill effect
   * from running on mount. The backend keeps the number and expiry a dose was linked
   * with — HSE edit expiry dates in place, and a dose already sent to NIIS and PCRS must
   * keep what it said in RXA.15/RXA.16 — so refilling them from the live batch would
   * show a date the record does not hold. Same idea as `filledRouteRef`: know what we
   * filled, so what the clinician did is never overwritten.
   */
  const filledBatchIdRef = useRef(
    vaccine?.vaccineBatchId ? vaccine.vaccineBatchId.toString() : ""
  );

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      type: vaccine?.type || "",
      vaccineName: vaccine?.vaccineName || "",
      batchNo: vaccine?.batchNo || "",
      vaccineBatchId: vaccine?.vaccineBatchId?.toString() || "",
      administrationMethod:
        vaccine?.administrationMethod || DEFAULT_VALUES.administrationMethod,
      dateGiven: getInitialDate(vaccine?.dateGiven),
      // Not defaulted to today. An expiry date is a property of the vial, not of the
      // event, and today asserts the dose expired on the day it was administered — which
      // passes the cross-field guard below, since that only rejects dateGiven AFTER
      // expiryDate. The field is required, so leaving it empty makes the clinician
      // supply the real one instead of silently storing a wrong one (KIN-1716).
      expiryDate: getInitialDate(vaccine?.expiryDate, { defaultToToday: false }),
      siteGiven: vaccine?.siteGiven || DEFAULT_VALUES.siteGiven,
      dose: vaccine?.doseMl?.toString() || "",
      reaction: vaccine?.reaction || DEFAULT_VALUES.reaction,
      reactionDate: vaccine?.reactionDate
        ? formatDateISO(vaccine.reactionDate)
        : "",
      notes: vaccine?.notes || "",
      vaccineCodeId: vaccine?.vaccineCodeId?.toString() || "",
      vaccineCodeName: vaccine?.vaccineCodeName || "",
      vaccineCodeCvxCode: vaccine?.vaccineCodeCvxCode || "",
      vaccineCodeMvxCode: vaccine?.vaccineCodeMvxCode || "",
      // The record carries no SNOMED of its own — PatientImmunisation has vaccineCode,
      // vaccineCodeCvxCode and vaccineCodeMvxCode, and nothing else — so there is
      // nothing to seed here. An earlier version read vaccineCodeCvxCode on the basis
      // that RSV products had no CVX code and so stored their SNOMED there; z_234 gave
      // them CVX 016 and 017, which ended that. The dose lock no longer depends on this
      // field: it resolves the product by id and reads its dose.
      vaccineCodeSnomed: "",
      // Preserved from the record on an edit, so a correction still describes the
      // specification the vaccination was originally reported under rather than
      // whichever is current. Filled from the risk-factor response when absent.
      specVersion: vaccine?.specVersion || "",
      doseNumberCode: vaccine?.doseNumberCode?.toString() || "1",
      // Reopened from the record rather than from a flag: the timestamp is the marker
      // (KIN-1883), so an existing "given elsewhere" dose opens with the option already on
      // and the five fields it never had still not demanded.
      givenElsewhere: !!vaccine?.administeredElsewhereAt,
      givenElsewhereBy: vaccine?.administeredElsewhereBy || "",
      vaccinationProgramme:
        vaccine?.vaccinationProgrammeCode || VACCINATION_PROGRAMME.primaryChildhood,
      outbreakCode: vaccine?.outbreakCode || "",
      administrationNotesCode:
        vaccine?.administrationNotesCode ||
        (vaccine?.type === "Pneumococcal" || vaccine?.type === "Pneumococcol"
          ? "X0070-1"
          : ""),
      additionalFactors: vaccine?.additionalFactors || [],
      immunocompromisedAssessment: vaccine?.immunocompromisedAssessment || "",
      ambulatoryStatus: vaccine?.ambulatoryStatus || "",
      riskFactorCodes: vaccine?.riskFactorCodes || [],
      clinicallyNecessary: vaccine?.clinicallyNecessary ?? true,
      consentGivenCode: vaccine?.consentGivenCode || "",
      ethnicityCode: vaccine?.ethnicityCode || "",
      vaccinator: "",
      healthcareWorker: vaccine?.healthcareWorker ?? null,
      patientDateOfBirth: patientDateOfBirth ?? null,
      pciScheduleCode: vaccine?.pciScheduleCode || "",
      pciVaccineType: vaccine?.pciVaccineType || "",
      vaccineManufacturer:
        vaccine?.vaccineManufacturer ||
        (vaccine?.vaccineCodeMvxCode
          ? getManufacturerByMvxCode(vaccine.vaccineCodeMvxCode).label
          : ""),
    },
  });

  const selectedDoseNumber = form.watch("doseNumberCode") || null;
  const selectedDateGiven = form.watch("dateGiven") || null;

  const {
    data: serverRiskFactors = { defaultedRisks: [], selectableRisks: [] },
    isLoading: isLoadingRisks,
    isError: isRiskFactorsError,
    error: riskFactorsError,
  } = useVaccineRiskFactors(
    currentPatientId,
    selectedSnomedCode,
    selectedVaccineType,
    true,
    selectedDoseNumber,
    selectedDateGiven
  );

  const watchedType = form.watch("type");
  const watchedDateGiven = form.watch("dateGiven");
  const watchedHealthcareWorker = form.watch("healthcareWorker");
  const watchedVaccineBatchId = form.watch("vaccineBatchId");
  const watchedGivenElsewhere = form.watch("givenElsewhere");

  /*
   * The batches a dose given on this date can be recorded from (KIN-1740). Keyed on the
   * date given rather than today, so moving the date back offers the batches that were
   * in date then.
   */
  const { data: vaccineBatches = [], isLoading: isLoadingVaccineBatches } =
    useVaccineBatches({ inDateOn: selectedDateGiven });

  // The product catalogue is already fetched by the form itself; this is the same cached
  // query, read here so a chosen batch can hand its product to the existing product
  // handler rather than filling dose, route and manufacturer a second way.
  const { data: healthlinkVaccines = [] } = useHealthlinkVaccines();

  /*
   * The batch an existing record was given from, so reopening it still shows the batch
   * even once HSE have dropped it or its expiry has passed. Built from the dose's own
   * stored number and expiry, which is what the backend keeps for an unchanged link.
   */
  const recordedBatchOption = useMemo(
    () =>
      toRecordedBatchOption({
        vaccineBatchId: vaccine?.vaccineBatchId,
        batchNo: vaccine?.batchNo,
        expiryDate: vaccine?.expiryDate
          ? formatDateISO(vaccine.expiryDate)
          : null,
      }),
    [vaccine?.vaccineBatchId, vaccine?.batchNo, vaccine?.expiryDate]
  );

  const isHcwQuestionAsked = isHealthcareWorkerQuestionAsked({
    vaccineType: watchedType,
    dateOfBirth: patientDateOfBirth,
    dateGiven: watchedDateGiven,
  });

  // The radio above the list already asks this; offering X0069-17 as well is the two
  // questions, one answer problem the code's own module documents.
  const riskFactors = useMemo(
    () =>
      withoutHealthcareWorkerOption({
        riskFactors: serverRiskFactors,
        isQuestionAsked: isHcwQuestionAsked,
        isEditMode,
      }),
    [serverRiskFactors, isHcwQuestionAsked, isEditMode]
  );

  useEffect(() => {
    const defaultVaccinator = getDefaultVaccinator();
    if (defaultVaccinator) {
      form.setValue("vaccinator", defaultVaccinator);
    }
  }, [staffInfo, isEditMode, getDefaultVaccinator, form]);

  useEffect(() => {
    if (!riskFactors?.defaultedRisks?.length) return;

    const newSelection = mergeDefaultedRiskCodes({
      currentCodes: form.getValues("riskFactorCodes") || [],
      defaultedRisks: riskFactors.defaultedRisks,
      isEditMode,
    });
    form.setValue("riskFactorCodes", newSelection);

    if (
      riskFactors.impliedAmbulatoryStatus &&
      !form.getValues("ambulatoryStatus")
    ) {
      form.setValue("ambulatoryStatus", riskFactors.impliedAmbulatoryStatus);
    }
  }, [riskFactors, isEditMode, form]);

  // The specification a vaccination is reported under is resolved by the backend from
  // the date it was given, and returned by the risk-factor endpoint — "send it back
  // unchanged as the immunisation's specVersion", per its own documentation. A
  // catalogue immunisation is rejected without one (@SpecVersionRequired); a manual one
  // is exempt, having no product to describe.
  //
  // An edit keeps the version the record was first reported under, so a correction still
  // describes the specification the vaccination was reported against. A new vaccination
  // follows its date given instead, re-stamping whenever that date changes.
  //
  // The difference matters because the two used to be one rule — "set it only when
  // empty" — and that stamped a backdated dose with the wrong version and then made it
  // permanently uneditable (KIN-2082). Entering a dose stamps the version for whatever
  // date is in the form at that moment; moving the date back to last year refetched the
  // right version, which the empty-check then refused to apply, and the backend rejects
  // every later save because the recorded version does not match the date
  // (HealthlinkValidationService: "the date given falls under version 4.3"). Its advice,
  // to re-select the Indications, cannot be followed on a programme that has none.
  //
  // Guarded separately from the effect above, which returns early when a vaccine carries
  // no default risk factors — RSV carries none at all, and it is exactly the programme
  // that needs this.
  useEffect(() => {
    const stamped = form.getValues("specVersion");
    const followsDateGiven = !isEditMode;

    if (riskFactors?.specVersion && (!stamped || followsDateGiven)) {
      form.setValue("specVersion", riskFactors.specVersion);
    }
    form.setValue(
      "immunocompromisedOptionsAvailable",
      !!riskFactors?.immunocompromisedStatuses?.length
    );
  }, [
    riskFactors?.specVersion,
    riskFactors?.immunocompromisedStatuses,
    isEditMode,
    form,
  ]);

  /*
   * A dose we did not give was not given from our stock (KIN-1883 + KIN-1740). The field
   * is hidden for one, but a link chosen before the option was ticked would still be
   * submitted — and the server copies the batch's number and expiry onto whatever names
   * it, refilling the two fields that feature deliberately leaves empty.
   */
  useEffect(() => {
    if (form.getValues("givenElsewhere") && form.getValues("vaccineBatchId")) {
      form.setValue("vaccineBatchId", "", { shouldValidate: false });
      filledBatchIdRef.current = "";
    }
  }, [watchedGivenElsewhere, form]);

  // The patient loads after the form mounts, so the default cannot hold this.
  useEffect(() => {
    form.setValue("patientDateOfBirth", patientDateOfBirth ?? null);
  }, [patientDateOfBirth, form]);

  // An answer must not survive the question. Change the date given, or the vaccine, so
  // that the patient falls outside 16-69 and the field disappears — the value it held
  // would otherwise still be sent, and a stale `false` makes an Indication mandatory
  // while a stale `true` waives one.
  useEffect(() => {
    if (!isHcwQuestionAsked && form.getValues("healthcareWorker") !== null) {
      form.setValue("healthcareWorker", null);
    }
  }, [isHcwQuestionAsked, form]);

  // The answer to the radio is the answer to X0069-17 — kept here so the Indication
  // count and the "at least one Indication" hint describe what will be submitted.
  useEffect(() => {
    const currentCodes = form.getValues("riskFactorCodes") || [];
    const nextCodes = applyHealthcareWorkerRiskCode({
      currentCodes,
      isQuestionAsked: isHcwQuestionAsked,
      healthcareWorker: watchedHealthcareWorker,
      isEditMode,
    });

    if (nextCodes !== currentCodes) {
      form.setValue("riskFactorCodes", nextCodes);
    }
  }, [isHcwQuestionAsked, watchedHealthcareWorker, isEditMode, form]);

  /*
   * Drops a chosen batch that no longer fits the product, and the number and expiry it
   * filled with it (KIN-1740).
   *
   * Batch to product is 1:1 (Phase 2 §4.4.4) and the backend refuses (400) a batch whose
   * product differs from the dose's, so a batch left behind by a product change is a
   * refusal waiting to happen — and, worse, a vial number sitting in the form looking
   * chosen for a vaccine it is not of. A batch matched to no product fits any dose and
   * stays; one we cannot resolve in the list is dropped, because its product is unknown
   * and guessing in its favour is what would reach the server.
   *
   * The number and expiry are cleared only when they are ours to clear — `filledBatchIdRef`
   * says we put them there. Anything typed by the clinician stands.
   */
  const clearBatchUnfitForProduct = useCallback(
    (nextVaccineCodeId) => {
      const batchId = form.getValues("vaccineBatchId");

      if (!batchId) return;

      const batch = findBatchById(vaccineBatches, batchId);
      const fitsNextProduct =
        !!batch &&
        (batch.vaccineCodeId === null ||
          batch.vaccineCodeId === undefined ||
          String(batch.vaccineCodeId) === String(nextVaccineCodeId));

      if (fitsNextProduct) return;

      form.setValue("vaccineBatchId", "", { shouldValidate: false });

      if (filledBatchIdRef.current === batchId) {
        form.setValue("batchNo", "", { shouldValidate: true });
        form.setValue("expiryDate", "", { shouldValidate: true });
      }

      filledBatchIdRef.current = "";
    },
    [form, vaccineBatches]
  );

  const handleHealthlinkVaccineSelect = useCallback(
    (selectedVaccine) => {
      clearBatchUnfitForProduct(selectedVaccine?.id ?? null);

      if (selectedVaccine) {
        form.setValue("vaccineCodeId", selectedVaccine.id.toString());
        form.setValue("vaccineCodeName", selectedVaccine.name);
        form.setValue("vaccineCodeCvxCode", selectedVaccine.cvxCode || selectedVaccine.snomedCode || "");
        form.setValue("vaccineCodeMvxCode", selectedVaccine.mvxCode);
        form.setValue("vaccineCodeSnomed", selectedVaccine.snomedCode);
        form.setValue("type", selectedVaccine.vaccineType);

        const vaccineName =
          selectedVaccine.saleProprietaryName || selectedVaccine.name;
        form.setValue("vaccineName", vaccineName, { shouldValidate: true });

        if (selectedVaccine.vaccineType) {
          form.setValue("type", selectedVaccine.vaccineType, {
            shouldValidate: true,
          });
        }

        if (selectedVaccine.mvxCode) {
          const manufacturer = getManufacturerByMvxCode(selectedVaccine.mvxCode);
          form.setValue("vaccineManufacturer", manufacturer.label, {
            shouldValidate: true,
          });
        } else {
          form.setValue("vaccineManufacturer", "", { shouldValidate: false });
        }

        // Only ever fills a dose we know; never clears one the clinician set. A null
        // doseMl means "no reference value", not zero, so it must not overwrite
        // anything.
        const productDose = selectedVaccine.doseMl;
        if (productDose !== null && productDose !== undefined) {
          form.setValue("dose", String(productDose), { shouldValidate: true });
        }

        /*
         * The route the same way (KIN-2115). The catalogue states it as a label
         * ("Intramuscular"), and what the form submits is the table 0162 code, so it is
         * translated on the way in. The backend fills and then enforces the same value
         * with a HARD_STOP, so a form that left it to be typed would be inviting a
         * refusal it already knows the answer to. Products with no stated route — flu,
         * COVID-19 and others — leave the field alone, to be chosen as before.
         */
        const productRouteCode = toAdministrationMethodCode(
          selectedVaccine.routeOfAdministration
        );

        if (productRouteCode) {
          form.setValue("administrationMethod", productRouteCode, {
            shouldValidate: true,
          });
        } else if (
          form.getValues("administrationMethod") === filledRouteRef.current
        ) {
          /*
           * The new product states no route and the field still holds the last one we filled
           * from a product — so it belongs to the vaccine that was selected before, not to
           * this one. Switching Rotavirus to a flu product left "Oral" sitting in an enabled
           * field, looking chosen; flu has no catalogue route, so the backend's route check
           * is silent for it and an oral flu dose would have gone to NIIS unremarked.
           *
           * Cleared only when it is ours to clear. A route the clinician typed differs from
           * what we filled, and that one stands.
           */
          form.setValue("administrationMethod", "", { shouldValidate: true });
        }

        filledRouteRef.current = productRouteCode || null;

        if (selectedVaccine.vaccineType === "PCI") {
          form.setValue("pciVaccineType", selectedVaccine.snomedSynonym || "");
        } else {
          form.setValue("pciVaccineType", "");
        }

        setSelectedSnomedCode(selectedVaccine.snomedCode || null);
        setSelectedVaccineType(selectedVaccine.vaccineType || null);
      } else {
        form.setValue("vaccineCodeId", "");
        form.setValue("vaccineCodeName", "");
        form.setValue("vaccineCodeCvxCode", "");
        form.setValue("vaccineCodeMvxCode", "");
        form.setValue("vaccineCodeSnomed", "");
        form.setValue("vaccineName", "");
        form.setValue("type", "");
        form.setValue("pciVaccineType", "");
        form.setValue("riskFactorCodes", []);
        form.setValue("doseNumberCode", "1");
        form.setValue("administrationNotesCode", "");
        form.setValue("additionalFactors", []);
        form.setValue("immunocompromisedAssessment", "");
        form.setValue("immunocompromisedOptionsAvailable", false);
        form.setValue("vaccineManufacturer", "");
        form.setValue("clinicallyNecessary", true);
        setSelectedSnomedCode(null);
        setSelectedVaccineType(null);
      }
    },
    [form, clearBatchUnfitForProduct]
  );

  /*
   * Picking a batch fills the form from it (KIN-1740, Phase 2 §4.4.4).
   *
   * Driven from the chosen id in form state rather than from the picker's change
   * handler, because the picker also writes that id by itself when the list narrows to a
   * single batch. Reading the id is the one place both paths meet.
   *
   * The order matters. The product goes in FIRST, through the same handler the product
   * search uses, so dose, route, manufacturer, trade name and type are filled exactly
   * once and exactly one way (KIN-2115 owns that behaviour and keeps it). Only then are
   * the batch's own number and expiry written, so nothing the product handler clears can
   * clear them afterwards.
   *
   * The two do not fight: the product handler drops a batch unfit for the product it is
   * given, and the product it is given here is the batch's own, so the batch survives
   * its own fill.
   */
  useEffect(() => {
    const batchId = watchedVaccineBatchId || "";

    // Nothing has changed since we last filled — a reopened record, or a re-render.
    if (batchId === filledBatchIdRef.current) return;

    if (!batchId) {
      // Cleared by hand. The number and expiry stay: they are now free text, and a
      // clinician who switches to typing should not have to retype what is already right.
      filledBatchIdRef.current = "";

      return;
    }

    const batch = findBatchById(vaccineBatches, batchId);

    // The record's own batch, re-offered from the record itself, is not in the list and
    // has nothing new to fill: the form already holds what the dose was recorded with.
    if (!batch) return;

    const batchValues = getBatchFormValues(batch);

    if (batchValues.vaccineCodeId) {
      const product = healthlinkVaccines.find(
        (candidate) => String(candidate.id) === batchValues.vaccineCodeId
      );

      // Only when it is a different product. Re-running it for the product already
      // chosen would overwrite a dose the clinician had corrected by hand.
      if (product && batchValues.vaccineCodeId !== form.getValues("vaccineCodeId")) {
        handleHealthlinkVaccineSelect(product);
      }
    }

    filledBatchIdRef.current = batchId;
    form.setValue("batchNo", batchValues.batchNo, { shouldValidate: true });
    form.setValue("expiryDate", batchValues.expiryDate, { shouldValidate: true });
  }, [
    watchedVaccineBatchId,
    vaccineBatches,
    healthlinkVaccines,
    handleHealthlinkVaccineSelect,
    form,
  ]);

  const handleClearRiskFactors = useCallback(() => {
    const defaultCodes = riskFactors.defaultedRisks
      .filter((r) => r.selected)
      .map((r) => r.code);

    // Clear empties the boxes the clinician ticked; the healthcare-worker code is not
    // one of them, it is the radio's answer, and it survives.
    form.setValue(
      "riskFactorCodes",
      applyHealthcareWorkerRiskCode({
        currentCodes: defaultCodes,
        isQuestionAsked: isHcwQuestionAsked,
        healthcareWorker: watchedHealthcareWorker,
        isEditMode,
      })
    );
  }, [
    riskFactors.defaultedRisks,
    form,
    isHcwQuestionAsked,
    watchedHealthcareWorker,
    isEditMode,
  ]);

  const setDateField = useCallback(
    (fieldName, date) => {
      form.setValue(fieldName, date ? toIsoDate(date) : "");
    },
    [form]
  );

  const selectedRiskCount = (form.watch("riskFactorCodes") || []).length;
  const totalAvailableRisks =
    riskFactors.defaultedRisks.length + riskFactors.selectableRisks.length;

  const loggedInDoctorId = staffInfo?.staffMember?.id?.toString() || "";
  const originalVaccinatorId = vaccine?.administrationStaffId?.toString() || "";
  const isDifferentDoctor =
    isEditMode &&
    !!loggedInDoctorId &&
    !!originalVaccinatorId &&
    loggedInDoctorId !== originalVaccinatorId;

  return {
    form,
    isEditMode,
    isDifferentDoctor,
    riskFactors,
    isLoadingRisks,
    isRiskFactorsError,
    riskFactorsError,
    selectedRiskCount,
    totalAvailableRisks,
    handleHealthlinkVaccineSelect,
    handleClearRiskFactors,
    setDateField,
    vaccineBatches,
    isLoadingVaccineBatches,
    recordedBatchOption,
  };
};
